import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HeartHandshakeIcon, PencilIcon } from 'lucide-react';
import { Button, Combobox, EmptyState, ErrorNotice, FactList, Field, Panel, Pill, SelectInput, TextArea, TextInput } from '@bcecloud/portal-ui';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { errorMessage } from '@/lib/api';
import {
  CHARITY_OUTCOME_LABELS,
  CHARITY_STATUS_LABELS,
  charityOutcomeTone,
  charityStatusTone,
  listCharityCandidates,
  readCharity,
  regionsText,
  saveCharityPlan,
  type Charity,
  type CharityOutcome,
} from '@/lib/charities';
import type { EventDetail } from '@/lib/events';

/** Talking points every plan starts with, from the tracking sheet's tab. */
const DEFAULT_TALKING_POINTS = [
  'Who the charity is and who they serve.',
  'What the team is building today and where it goes afterwards.',
  'How the handover works at the end: who speaks, who collects.',
].join('\n');

/**
 * The event's charity: the directory charity it is paired with, how that
 * went, and the working details (speaker, arrival, pickup, talking points)
 * a charity note used to carry as text. Internal roles edit.
 */
export function CharityPanel({ detail, canEdit }: { readonly detail: EventDetail; readonly canEdit: boolean }): React.ReactElement {
  const [editing, setEditing] = useState(false);
  const plan = detail.charityPlan;
  const charity = useQuery({
    queryKey: ['charity', plan?.charityId],
    queryFn: ({ signal }) => readCharity(plan?.charityId ?? '', signal),
    enabled: Boolean(plan?.charityId),
  });
  const synced = (detail.charity ?? []).map((entry) => entry.name).filter(Boolean).join(', ');

  return (
    <Panel className="p-5">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-portal-micro font-extrabold tracking-[1px] text-portal-muted uppercase">Charity</h2>
        {canEdit ? (
          <Button guide="charity-choose" variant="secondary" size="compact" onClick={() => setEditing(true)}>
            <PencilIcon aria-hidden className="size-4" /> {plan?.charityId ? 'Edit plan' : 'Choose charity'}
          </Button>
        ) : null}
      </div>
      {plan?.charityId ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            {charity.data ? (
              <Link to={`/charities/${charity.data._id}`} className="text-portal-small font-bold text-brand hover:underline">
                {charity.data.name}
              </Link>
            ) : (
              <span className="text-portal-small font-bold">Charity</span>
            )}
            {plan.outcome ? <Pill tone={charityOutcomeTone(plan.outcome)}>{CHARITY_OUTCOME_LABELS[plan.outcome]}</Pill> : null}
            {charity.data ? <Pill tone={charityStatusTone(charity.data.status)}>{CHARITY_STATUS_LABELS[charity.data.status]}</Pill> : null}
          </div>
          <div className="mt-3">
            <FactList
              columns={2}
              showEmpty
              facts={[
                { label: 'Speaker', value: [plan.speaker?.name, plan.speaker?.cell].filter(Boolean).join(' · ') },
                { label: 'Arrival', value: plan.arrivalAt },
                { label: 'Pickup plan', value: plan.pickupPlan },
                { label: 'Note', value: plan.note },
              ]}
            />
          </div>
          {plan.talkingPoints ? (
            <div className="mt-3">
              <p className="text-portal-micro font-extrabold tracking-[0.6px] text-portal-muted uppercase">Talking points</p>
              <p className="mt-1 whitespace-pre-line text-portal-small">{plan.talkingPoints}</p>
            </div>
          ) : null}
        </>
      ) : (
        <EmptyState
          icon={<HeartHandshakeIcon aria-hidden className="size-6" />}
          title={synced ? `Salesforce lists ${synced}` : 'No charity chosen'}
          message={synced ? 'Pair it with the directory to track the outcome, speaker and pickup here.' : 'Pick from the directory — approved and nearby charities come first.'}
          {...(canEdit ? { action: <Button onClick={() => setEditing(true)}>Choose charity</Button> } : {})}
        />
      )}
      {editing ? <CharityPlanDialog detail={detail} onClose={() => setEditing(false)} /> : null}
    </Panel>
  );
}

function CharityPlanDialog({ detail, onClose }: { readonly detail: EventDetail; readonly onClose: () => void }): React.ReactElement {
  const queryClient = useQueryClient();
  const plan = detail.charityPlan;
  const candidates = useQuery({ queryKey: ['charity-candidates', detail._id], queryFn: ({ signal }) => listCharityCandidates(detail._id, signal) });
  const [charityId, setCharityId] = useState(plan?.charityId ?? '');
  const [outcome, setOutcome] = useState<CharityOutcome>(plan?.outcome ?? 'pending');
  const [speakerName, setSpeakerName] = useState(plan?.speaker?.name ?? '');
  const [speakerCell, setSpeakerCell] = useState(plan?.speaker?.cell ?? '');
  const [arrivalAt, setArrivalAt] = useState(plan?.arrivalAt ?? '');
  const [pickupPlan, setPickupPlan] = useState(plan?.pickupPlan ?? '');
  const [talkingPoints, setTalkingPoints] = useState(plan?.talkingPoints ?? DEFAULT_TALKING_POINTS);
  const [note, setNote] = useState(plan?.note ?? '');

  const save = useMutation({
    mutationFn: (clear: boolean) =>
      saveCharityPlan(detail._id, {
        charityId: clear ? null : charityId,
        outcome,
        speaker: { name: speakerName.trim(), cell: speakerCell.trim() },
        arrivalAt: arrivalAt.trim(),
        pickupPlan: pickupPlan.trim(),
        talkingPoints: talkingPoints.trim(),
        note: note.trim(),
      }),
    onSuccess: async (_result, clear) => {
      toast.success(clear ? 'Charity cleared' : 'Charity plan saved');
      await queryClient.invalidateQueries({ queryKey: ['event', detail._id] });
      onClose();
    },
  });
  const optionLabel = (charity: Charity): string => {
    const where = regionsText(charity);
    return `${charity.name}${charity.status !== 'approved' ? ` (${CHARITY_STATUS_LABELS[charity.status].toLowerCase()})` : ''}${where ? ` — ${where}` : ''}`;
  };

  return (
    <Dialog open onOpenChange={(next) => (!next && !save.isPending ? onClose() : undefined)}>
      <DialogContent className="max-h-[calc(100vh-4rem)] overflow-y-auto sm:max-w-xl">
        <DialogTitle>{plan?.charityId ? 'Edit charity plan' : 'Choose a charity'}</DialogTitle>
        <form
          className="mt-2 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (charityId && !save.isPending) save.mutate(false);
          }}
        >
          <Field guide="charity-plan-charity" label="Charity" htmlFor="plan-charity" required hint="Approved and nearby charities are listed first.">
            {candidates.isPending ? (
              <p className="text-portal-small text-portal-muted">Loading the directory…</p>
            ) : (
              <Combobox
                id="plan-charity"
                options={(candidates.data ?? []).map((charity) => ({ value: charity._id, label: optionLabel(charity) }))}
                value={charityId}
                onChange={setCharityId}
                placeholder="Search the directory"
                noneLabel="No charity"
              />
            )}
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field guide="charity-plan-outcome" label="Outcome" htmlFor="plan-outcome">
              <SelectInput id="plan-outcome" value={outcome} onChange={(event) => setOutcome(event.target.value as CharityOutcome)}>
                {(Object.keys(CHARITY_OUTCOME_LABELS) as CharityOutcome[]).map((key) => (
                  <option key={key} value={key}>
                    {CHARITY_OUTCOME_LABELS[key]}
                  </option>
                ))}
              </SelectInput>
            </Field>
            <Field guide="charity-plan-arrival" label="Arrival" htmlFor="plan-arrival">
              <TextInput id="plan-arrival" value={arrivalAt} placeholder="4:45pm Central" onChange={(event) => setArrivalAt(event.target.value)} />
            </Field>
            <Field guide="charity-plan-speaker" label="Speaker / rep" htmlFor="plan-speaker">
              <TextInput id="plan-speaker" value={speakerName} onChange={(event) => setSpeakerName(event.target.value)} />
            </Field>
            <Field label="Speaker cell" htmlFor="plan-cell">
              <TextInput id="plan-cell" type="tel" value={speakerCell} onChange={(event) => setSpeakerCell(event.target.value)} />
            </Field>
          </div>
          <Field guide="charity-plan-pickup" label="Pickup plan" htmlFor="plan-pickup">
            <TextArea id="plan-pickup" rows={2} value={pickupPlan} onChange={(event) => setPickupPlan(event.target.value)} />
          </Field>
          <Field guide="charity-plan-talking-points" label="Talking points" htmlFor="plan-talking" hint="What the facilitator says at the handover.">
            <TextArea id="plan-talking" rows={4} value={talkingPoints} onChange={(event) => setTalkingPoints(event.target.value)} />
          </Field>
          <Field guide="charity-plan-note" label="Note" htmlFor="plan-note">
            <TextArea id="plan-note" rows={2} value={note} onChange={(event) => setNote(event.target.value)} />
          </Field>
          {save.isError ? <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} /> : null}
          <div className="flex items-center justify-between gap-3">
            {plan?.charityId ? (
              <Button type="button" variant="secondary" onClick={() => save.mutate(true)} disabled={save.isPending}>
                Clear charity
              </Button>
            ) : (
              <span />
            )}
            <span className="flex items-center gap-3">
              <Button type="button" variant="secondary" onClick={onClose} disabled={save.isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={!charityId || save.isPending}>
                {save.isPending ? 'Saving…' : 'Save plan'}
              </Button>
            </span>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
